import { formatDate } from "../../utils/date.js";
import IssuerLogo from "../shared/IssuerLogo.jsx";

// The GPS quote letter, replacing QuoteReport.rpt (standard) and
// QuoteReportEx.rpt ("all details" — adds unit/qty/margin columns, internal).
// Section order mirrors the Crystal layout: letterhead → date → addressee →
// Re block → intro → quote notes (legacy CustomDisplayField1 position) →
// Cost Breakdown → totals → guarantee/validity → sign-off → INSTRUCTIONS
// acceptance block → return-email line. Same print mechanism as InvoiceView.
//
// PRINT OPTIONS (1.0 QuoteReport.aspx options panel, ported 2026-07-22 —
// quotes.md §7a): grouping/summary/wording/price visibility are pure display
// choices over the one document payload (services/quoteReport.js provides the
// summaries + overheads split server-side). 1.0 → 2.0 mapping:
//   Display Header    → headings: "location" (DEFAULT — Floor–Room then Work
//                       Centre, 1.0's `Ignore`) | "quote" (the quote's own
//                       header lines inline, 1.0 `ShowBreakdown`); and
//                       detail: "summaryOnly" is 1.0's `HeaderOnly`.
//   Order By          → NOT an option. Lines always print in entered order,
//                       which is 1.0's default (`Entered in Quote Screen`). Its
//                       other choice, a floor/room/work-centre sort, is not
//                       ported — see DEFAULT_PRINT_OPTIONS.
//                       Row-building lives in quoteRows.js and is tested there.
//   (QuoteReportEx)   → allDetails: the internal print with unit/qty/margin
//                       columns. Defaults OFF and stays that way — see below.
//   Total Options     → summary: "none" | "work_centre" | "room" |
//                       "work_centre_room" | "header" (appended after totals)
//   Remove Costs      → hidePrices (no amounts, no totals, price-free intro)
//   Show Quantity     → showQty: false | "unit" (qty + unit, 1.0's shape;
//                       true = the same) | "qty" (the number alone) — in the
//                       price column's place, on a quote OR a Scope of Works
//                       (Robyn 2026-09-10: "you can't do quantity without the
//                       price showing"; Robert: Qty & Unit or just Qty)
//   Seperate Overheads→ separateOverheads — 2.0 FIX: the excluded Overheads
//                       amount prints as its own pre-totals row; totals stay
//                       complete (1.0 silently dropped it)
//   Estimate Mode     → estimate (quote→estimate wording)
//   Hide Zero Dollars → hideZero (default ON, as in 1.0)
//   Table Format      → bordered (grid styling vs open letter)

import { buildQuoteRows, splitTotals, splitHeaderText } from "./quoteRows.js";

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

export const DEFAULT_PRINT_OPTIONS = {
  summary: "none",
  detail: "full",
  // HEADINGS, not order — and the rename matters, because conflating the two is
  // what made this wrong. 1.0 has two INDEPENDENT controls and its defaults are
  // "Order By: Entered in Quote Screen" AND "Display Header Options: Ignore",
  // so a default 1.0 quote is lines in entered order UNDER floor–room headings.
  // 2.0 had them as one dropdown offering "Lines as entered" OR "Lines by
  // location", which made the two mutually exclusive and meant asking for the
  // headings appeared to give up the entered order (Robert 2026-08-11: "lines
  // as entered should be the default option imo" — it is, and now it always is).
  //
  // Lines are ALWAYS in entered order here; buildQuoteRows never sorts. This
  // picks which headings get drawn over them.
  headings: "location", // 1.0 `Ignore` — Floor–Room, then Work Centre under it
  // NOT PORTED: 1.0's "Order By: Default" (sort by floor, room, work centre).
  // It is the non-default option on a control whose default we now match, so it
  // is a missing OPTION rather than a missing behaviour. Add it as a real sort
  // if anyone asks.
  // SCOPE OF WORKS (KS 2026-09-08, marked-up 76437): the works and the notes
  // without the quote around them — title, no TO block, RE box on the left
  // under a bold SCOPE OF WORKS, no intro or "Cost Breakdown" label, no
  // amounts or section totals, no validity note or sign-off; the guarantee
  // paragraph and the quote notes stay. Implies hidePrices. Set by the
  // Scope of Works mode (quotePrintModes.js). 1.0 never had this — its
  // "Remove Costs" was a quote with the money taken out.
  sow: false,
  hidePrices: false,
  showQty: false,
  separateOverheads: false,
  estimate: false,
  hideZero: true,
  bordered: false,
  // The Vero layout, as three independent switches (per-insurer DEFAULTS come
  // from the server's suggested_print_options; the full per-company template
  // registry later supplies the same flags as data):
  //   roomTotals     — numbered 1.00/2.00 sections, each closing "Total for …"
  //   separateMargin — line amounts at COST, one Builders Overheads and Profit
  //                    (Margin) line in the summary ($ + margin-% of sell).
  //                    ONE switch on purpose: costs without the margin line
  //                    don't add up, sell prices plus a margin line double-
  //                    count — only the reconciling document is printable.
  //   rateColumns    — the qty-columns line shape. TRI-STATE (KS 2026-08-20):
  //                    false = the standard Description/Price shape;
  //                    "unit" = Qty / Unit / Amount (NO Rate column);
  //                    "rate" = Qty / Unit / Rate / Amount (Vero/AA shape).
  //                    A legacy boolean true (older saved print blobs, the
  //                    insurer suggestions before this) reads as "rate".
  //                    Independent of separateMargin (AA's template wants it
  //                    with amounts at sell — honest there because AA practice
  //                    prices lines at 0% markup, so rate × qty = amount; on a
  //                    marked-up line the row won't multiply, which the option
  //                    title says).
  roomTotals: false,
  separateMargin: false,
  rateColumns: false,
  // 1.0's QuoteReportEx.rpt: unit price, quantity, margin % and margin value
  // beside each line. It lived as a switch on the TAB until 2026-08-11, next to
  // the Print button, which put one print choice somewhere other than the print
  // options — and it never did anything on the page, since only the preview
  // reads it (Robert: "why would we not put all details in the print part").
  //
  // DEFAULTS OFF, deliberately and permanently: off is the client quote,
  // on exposes our margins. Both directions of failure are not equal — a client
  // seeing internal margins cannot be undone, so it has to be a deliberate act
  // every time rather than a setting someone left on.
  allDetails: false,
};

const SUMMARY_TITLES = {
  work_centre: "Summary by Work Centre",
  room: "Summary by Room",
  work_centre_room: "Summary by Work Centre within Room",
  header: "Summary by Quote Header",
};

const ISSUER = {
  name: "Grayburn Property Services Ltd",
  tagline: '"The Insurance Reinstatement Specialists, Quality & Service 100% Guaranteed"',
  address: ["2D Rawhiti Rd, One Tree Hill, Auckland", "PO Box 11-551, Ellerslie"],
  phone: "09-622 3900",
  email: "reception@gps1.co.nz",
  web: "www.gps1.co.nz",
};

// The header title/body split lives with the row builder (pure, tested there); re-exported so the test keeps its import.
export { splitHeaderText };

// Scope lines a Scope of Works leaves out: the template's closing "NOTE :
// QUOTE IS SUBJECT TO MATERIALS AVAILABILITY…" is about the quote, not the
// works (KS struck it on 76437; ~2,850 jobs carry it verbatim). Exported for
// the test. Pattern, not position, so a retyped version still goes.
export function sowScopeLines(lines = []) {
  return lines.filter((l) => !/QUOTE\s+IS\s+SUBJECT\s+TO/i.test(String(l || "")));
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <tr>
      <td className="pe-3 text-muted align-top" style={{ whiteSpace: "nowrap" }}>{label}</td>
      <td style={{ whiteSpace: "pre-wrap" }}>{children}</td>
    </tr>
  );
}


function QuoteView({ doc, options = DEFAULT_PRINT_OPTIONS }) {
  if (!doc) return null;
  const { claim, lines, notes, totals, scope_lines: scopeLines = [] } = doc;
  const o = { ...DEFAULT_PRINT_OPTIONS, ...options };
  // A Scope of Works hides the money; quantities are its own choice.
  if (o.sow) { o.hidePrices = true; o.allDetails = false; }
  const qtyOnly = o.showQty === "qty";
  const printedScope = o.sow ? sowScopeLines(scopeLines) : scopeLines;
  const word = o.estimate ? "estimate" : "quote";
  // "All details" money columns make no sense on a price-free print.
  const showDetailCols = o.allDetails && !o.hidePrices;

  // The detail rows this print shows (option-filtered, option-ordered).
  // separateMargin overrides the overheads fold: at cost, the fee lines ARE
  // ordinary cost rows (Vero's own template shows them as sections).
  const overheadsOut = (l) =>
    o.separateMargin || !(o.separateOverheads && !l.is_header && l.work_centre_name === "Overheads");
  const body = lines.filter(overheadsOut);
  // Which rows exist, and in what order — see quoteRows.js. Rendering them is
  // this component's job; deciding them is tested separately.
  const rows = buildQuoteRows(
    body,
    { ...o, amountKey: o.separateMargin ? "gross_value" : "net_value" },
    doc.summaries?.header || []
  );
  const split = splitTotals(lines);
  const rateCols = o.rateColumns && !showDetailCols && !o.hidePrices && !o.showQty
    ? (o.rateColumns === "unit" ? "unit" : "rate") // boolean true = the historical rate shape
    : false;
  const showRate = rateCols === "rate";

  // No amount column at all when prices are hidden and quantities are not
  // shown: the empty column still took its 120px and the descriptions wrapped
  // early (KS 2026-09-08: "make the print go all the way to the margin").
  const amountCol = !(o.hidePrices && !o.showQty);
  const colCount = showDetailCols ? 7 : rateCols ? (showRate ? 5 : 4) : amountCol ? 2 : 1;
  const amountOf = (l) => (o.separateMargin ? l.gross_value : l.net_value);
  const priceCell = (l) => {
    if (o.showQty) {
      const qty = Number(l.quantity) || "";
      return qtyOnly ? String(qty) : `${qty} ${l.quantity_type || ""}`.trim();
    }
    if (o.hidePrices) return "";
    if (o.hideZero && !Number(amountOf(l))) return "";
    return money(amountOf(l));
  };

  const summaryRows = o.summary !== "none" && !o.hidePrices ? doc.summaries?.[o.summary] || [] : [];

  return (
    <div className="print-document quote-doc bg-white text-dark p-4" style={{ maxWidth: 820, margin: "0 auto" }}>
      {/* The whole letter sits in ONE table whose <tfoot> is the running identity
          line. A browser repeats a table footer at the bottom of every printed
          page AND reserves its height there — which `position: fixed` (the
          first version, 2026-08-12) did not: fixed only floats over the page,
          so the last line of text on each sheet slid underneath it and printed
          cut in half (KS, 76597, 2026-09-11: "words being cut off"). Karen
          Renall's ask stands — every sheet of a separated quote names the job.
          On screen the footer row is hidden (app.css) and the table is
          invisible layout. */}
      <table className="quote-page">
        <tfoot>
          <tr>
            <td>
              <div className="quote-running-foot">
                GPS#{claim.claim_number}
                {claim.client_name ? ` · ${claim.client_name}` : ""}
                {" · "}Your Ref: {claim.your_ref || "—"}
              </div>
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td>
      {/* Letterhead */}
      <div className="d-flex justify-content-between align-items-start mb-1">
        {/* Logo LEFT, wording to its right (Robert 2026-08-04) — a letterhead,
            not a stacked logo with a caption under it. */}
        <div className="d-flex align-items-center gap-3">
          <IssuerLogo className="" />
          <div>
            <div className="fs-4 fw-bold">{ISSUER.name}</div>
            <div className="small fst-italic text-muted">{ISSUER.tagline}</div>
          </div>
        </div>
        {/* The title never wraps (SCOPE OF WORKS broke onto two lines, Robert
            2026-09-08); the tagline on the left is the one that gives way. */}
        <div className="text-end flex-shrink-0 ms-3">
          <div className="fs-3 fw-bold text-uppercase text-nowrap">{o.sow ? "Scope of Works" : o.estimate ? "Estimate" : "Quotation"}</div>
          {/* Full instant, not a sliced UTC date — the sliced form printed
              YESTERDAY on NZ mornings (InvoiceDocument was already correct). */}
          <div className="small text-muted">Date {formatDate(new Date())}</div>
        </div>
      </div>

      {/* Addressee / Re — on a Scope of Works there is no addressee (KS's
          markup: TO block struck out, "MOVE THIS OVER HERE"): the RE box sits
          on the left under a bold SCOPE OF WORKS heading. */}
      <div className="row my-4">
        {o.sow ? null : (
          <div className="col-6">
            <div className="text-uppercase small text-muted fw-semibold mb-1">To</div>
            <div className="fw-semibold">{claim.assessor_company_name || claim.insurer_name || "—"}</div>
            {claim.insurer_name && claim.assessor_company_name ? (
              <div className="small">{claim.insurer_name}</div>
            ) : null}
            {claim.assessor_name ? <div className="small">Attention: {claim.assessor_name}</div> : null}
          </div>
        )}
        <div className="col-6">
          {o.sow ? (
            <div className="fw-bold mb-1">SCOPE OF WORKS</div>
          ) : (
            <div className="text-uppercase small text-muted fw-semibold mb-1">Re</div>
          )}
          <table className="small"><tbody>
            <Field label="Client">{claim.client_name}</Field>
            <Field label="Address">{claim.street_address}</Field>
            <Field label="Your Ref">{claim.your_ref}</Field>
            <Field label="Insurer">{claim.insurer_name}</Field>
            <Field label="Claim #">{claim.insurance_claim_number}</Field>
            <Field label="Our Ref">GPS#{claim.claim_number}</Field>
          </tbody></table>
        </div>
      </div>

      {/* Intro (legacy wording; Remove Costs drops the price clause, exactly
          as 1.0's HandleUndertakeWorksDisplay did). None on a Scope of Works. */}
      {o.sow ? null : (
        <>
          <p className="mb-1">Thank you for the opportunity to {word} on works required at the above property.</p>
          {o.hidePrices ? (
            <p>
              Following the inspection of the damaged property at {claim.street_address || "the above address"},
              the following works are submitted to be undertaken as detailed below:
            </p>
          ) : (
            <p>
              Following the inspection of the damaged property at {claim.street_address || "the above address"},{" "}
              <strong>{o.estimate ? "an estimate" : "a quotation"} of {money(totals.subtotal)} excluding GST</strong>{" "}
              is submitted to undertake works as detailed below:
            </p>
          )}
        </>
      )}

      {/* SCOPE OF WORKS — the words describing the work, before the money that
          prices it. It was missing from the printed quote entirely (Karen
          Renall 2026-08-05, job 76141): the document was assembled from the
          priced lines only, so the scope never reached the page. That it belongs
          here is not a guess — the [Replace Me] reminders that reached insurers
          live in these lines, so 1.0 must print them. */}
      {printedScope.length > 0 ? (
        <div className="mb-3">
          <div className="fw-bold mb-1">Scope of Works:</div>
          {/* PLAIN LINES — no numbers, no bullets. This was an <ol>, so the
              browser numbered every line and a client's scope read "1. 2. 3."
              (Marissa 2026-08-11; Karen Sweatman: "yes, remove numbers from
              beginning of lines").
              1.0 settles the question: QuoteReport.aspx.cs line 118 does
              `lblScopeOfWorks.Text = DisplayNewLines(log.QuoteDetails)` and then
              replaces "\n" with "<br>" — plain text, line breaks only, no list
              markup of any kind. 2.0 holds the same content as rows rather than
              one blob, so each row is its own line; the output matches. */}
          <div className="mb-0">
            {/* A BLANK scope line is a paragraph break the estimator typed
                (76513: the empty row before GENERAL COMMENT/SITE OBSERVATIONS)
                — an empty div collapses to nothing, so it holds a nbsp to keep
                its height. The server keeps interior blanks and drops only
                leading/trailing ones (quoteReport.js shapeScopeLines). */}
            {printedScope.map((line, i) => (
              <div key={i} style={{ whiteSpace: "pre-wrap" }}>{line.trim() ? line : "\u00A0"}</div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Cost Breakdown — no label on a Scope of Works (struck out in KS's markup) */}
      {o.sow ? null : <div className="fw-bold mb-1">Cost Breakdown:</div>}
      <table className={`table table-sm align-middle${o.bordered ? " table-bordered" : ""}`}>
        <thead className={o.bordered ? "table-light" : undefined}>
          <tr style={o.bordered ? undefined : { borderBottom: "2px solid #dee2e6" }}>
            <th>Description</th>
            {showDetailCols ? (
              <>
                <th className="text-end" style={{ width: 90 }}>Price</th>
                <th className="text-end" style={{ width: 70 }}>Qty</th>
                <th className="text-end" style={{ width: 100 }}>Value</th>
                <th className="text-end" style={{ width: 80 }}>% Margin</th>
                <th className="text-end" style={{ width: 90 }}>Margin</th>
              </>
            ) : null}
            {rateCols ? (
              <>
                <th className="text-end" style={{ width: 60 }}>Qty</th>
                <th style={{ width: 55 }}>Unit</th>
                {showRate ? <th className="text-end" style={{ width: 90 }}>Rate</th> : null}
              </>
            ) : null}
            {amountCol ? (
              <th className="text-end" style={{ width: o.showQty ? 90 : 120 }}>
                {o.showQty ? (qtyOnly ? "Qty" : "Quantity") : rateCols || o.separateMargin ? "Amount" : "Price"}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) =>
            r.kind === "heading" ? (
              /* A quote header line printed inside the location layout
                 (quoteRows.js) outranks the floor–room heading under it:
                 bold where the location is semibold, and more space above. */
              <tr key={i}>
                <td colSpan={colCount} className={`text-uppercase ${r.header ? "fw-bold pt-3" : "fw-semibold pt-2"}`}>
                  {r.num ? `${r.num}  ` : ""}{r.label}
                </td>
              </tr>
            ) : r.kind === "sectionTotal" ? (
              /* The Vero layout's "Total for Bedroom 2" row — sums what the
                 lines above actually SHOW (cost under separateMargin). With no
                 amount column there is nothing to total, so no row (KS's
                 markup struck "Total for Ground Floor" on the price-free print). */
              !amountCol ? null : (
              <tr key={i} style={{ borderTop: "1.5px solid #dee2e6" }}>
                <td colSpan={colCount - 1} className="fw-semibold text-end pe-3">Total for {r.label}</td>
                <td className="text-end text-nowrap fw-semibold">{o.hidePrices ? "" : money(r.total)}</td>
              </tr>
              )
            ) : r.kind === "subheading" ? (
              /* Work centre, under its Floor – Room. 1.0 gives the two levels
                 separate classes (qlh1/qlh2) and so do we, but by TYPE rather
                 than by position: the location is uppercase, the work centre is
                 sentence case, the lines are regular weight. Three levels, no
                 indent.
                 The indent went on 2026-08-12 (Robert): it put the work centre
                 further right than the lines BELOW it, so a trade read as
                 subordinate to its own items — and it stole width from
                 descriptions that already wrap. */
              <tr key={i}>
                <td colSpan={colCount} className="fw-semibold pt-2">{r.label}</td>
              </tr>
            ) : r.kind === "summary" ? (
              /* The header's FIRST LINE is its title and prints bold, the rest
                 is its description. Three ways the split is written: 1.0's
                 habit of two spaces (imported verbatim), 2.0's editor with a
                 newline (KS on 76511, 2026-09-07 — those printed unbolded
                 until this knew about it), or no description at all, where
                 the whole line is the title. */
              <tr key={i}>
                <td style={{ whiteSpace: "pre-wrap" }}>
                  {(() => {
                    const [title, body] = splitHeaderText(r.label);
                    return body ? (
                      <>
                        <div className="fw-semibold">{title}</div>
                        <div>{body}</div>
                      </>
                    ) : (
                      <span className="fw-semibold">{title}</span>
                    );
                  })()}
                </td>
                {colCount > 2 ? <td colSpan={colCount - 2} /> : null}
                {/* a header line printed as an item (total null) has no amount; a $0 section loses its $0.00 under Hide zero amounts */}
                {amountCol ? <td className="text-end text-nowrap">{o.hidePrices || r.total == null || (o.hideZero && !Number(r.total)) ? "" : money(r.total)}</td> : null}
              </tr>
            ) : (
              <tr key={i}>
                <td style={{ whiteSpace: "pre-wrap" }}>{r.l.description || "—"}</td>
                {showDetailCols ? (
                  <>
                    <td className="text-end text-nowrap">{money(r.l.unit_price)}</td>
                    <td className="text-end text-nowrap">{Number(r.l.quantity) || ""}</td>
                    <td className="text-end text-nowrap">{money(r.l.gross_value)}</td>
                    <td className="text-end text-nowrap">{Number(r.l.margin_percent)}%</td>
                    <td className="text-end text-nowrap">{money(r.l.margin_value)}</td>
                  </>
                ) : null}
                {rateCols ? (
                  <>
                    <td className="text-end text-nowrap">{Number(r.l.quantity) || ""}</td>
                    <td>{r.l.quantity_type || ""}</td>
                    {showRate ? (
                      <td className="text-end text-nowrap">{Number(r.l.unit_price) ? money(r.l.unit_price) : ""}</td>
                    ) : null}
                  </>
                ) : null}
                {amountCol ? <td className="text-end text-nowrap">{priceCell(r.l)}</td> : null}
              </tr>
            )
          )}
          {rows.length === 0 ? (
            <tr><td colSpan={colCount} className="text-center text-muted">No quote lines.</td></tr>
          ) : null}
        </tbody>
      </table>

      {/* Totals — hidden entirely by Remove Costs (1.0). */}
      {o.hidePrices ? null : (
        <div className="d-flex justify-content-end mb-4">
          <table style={{ minWidth: 300 }}><tbody>
            {o.separateOverheads && !o.separateMargin ? (
              <tr>
                <td className="text-muted pe-4">Overheads (Preliminaries &amp; Generals)</td>
                <td className="text-end">{money(doc.overheads_total)}</td>
              </tr>
            ) : null}
            {o.separateMargin ? (
              /* The Vero summary (their contract's Appendix I): costs subtotal,
                 then Builders Overheads and Profit as MARGIN — dollars plus
                 margin-of-sell percent (57% markup prints 36.3%; Vero asked
                 for margin, not markup) — reconciling exactly to the same
                 ex-GST total every other layout prints. */
              <>
                <tr>
                  <td className="text-muted pe-4">Sub Total</td>
                  <td className="text-end">{money(split.cost)}</td>
                </tr>
                <tr>
                  <td className="text-muted pe-4">Builders Overheads and Profit ({split.marginPct}%)</td>
                  <td className="text-end">{money(split.margin)}</td>
                </tr>
              </>
            ) : null}
            <tr>
              <td className="text-muted pe-4">Total (ex GST)</td>
              <td className="text-end fw-semibold">{money(totals.subtotal)}</td>
            </tr>
            <tr>
              <td className="text-muted pe-4">GST ({Math.round(totals.gst_rate * 100)}%)</td>
              <td className="text-end">{money(totals.gst)}</td>
            </tr>
            <tr className="fw-bold" style={{ borderTop: "2px solid #212529" }}>
              <td className="pe-4">Total (incl GST)</td>
              <td className="text-end">{money(totals.total)}</td>
            </tr>
          </tbody></table>
        </div>
      )}

      {/* Quote notes — AFTER the totals, BEFORE the guarantee paragraph
          (Karen 2026-07-22: "under the Total (incl GST) figure & before the
          paragraph that starts with All GPS supplied workmanship"). */}
      {notes.length > 0 ? (
        <div className="mb-3 quote-notes">
          {notes.map((n) => (
            <div key={n.id} className={n.is_header ? "fw-semibold mt-2" : ""} style={{ whiteSpace: "pre-wrap" }}>
              {n.description}
            </div>
          ))}
        </div>
      ) : null}

      {/* Appended summary block (1.0 "Total Options"). */}
      {summaryRows.length > 0 ? (
        <div className="mb-4">
          <div className="fw-bold mb-1">{SUMMARY_TITLES[o.summary]}</div>
          <table className={`table table-sm${o.bordered ? " table-bordered" : ""}`} style={{ maxWidth: 460 }}>
            <tbody>
              {summaryRows.map((s) => (
                <tr key={s.label}>
                  <td>{s.label}</td>
                  <td className="text-end text-nowrap" style={{ width: 120 }}>{money(s.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Guarantee + validity (legacy wording; Estimate Mode swaps quote→estimate
          exactly like 1.0's .quote-text spans). */}
      <div className="small mb-3">
        <p className="mb-2">
          All GPS supplied workmanship comes with a 100% unconditional guarantee for a period of 10 years.
          If any product/fixture/fitting installed becomes defective during the manufacturer's warranty
          period we will arrange manufacturer to either repair or replace any defective part. If manufacturer
          does not have a 'back to base' warranty, additional costs will apply to return &amp; replace item.
          Should further detail be required please do not hesitate to contact the writer.
        </p>
        {o.sow ? null : (
          <p className="mb-0">
            <span className="fw-semibold">Please Note:</span> This {word} is valid for sixty days and subject
            to availability of materials and/or equivalent. This {word} assumes that existing structure
            complies with NZ3604. Variations or non-compliance may result in additional costs.
          </p>
        )}
      </div>

      {/* Signed by the person who INSPECTED the job, with their LBP licence, and
          by the supervisor when no inspector is assigned. 1.0 could only ever
          print "Supervisor" because the licence was buried in the person's name
          with nowhere to lay it out. The role line still says which of the two
          you are reading, so a quote never claims an inspection nobody did. */}
      {o.sow ? null : (
      <div className="mb-4">
        <div>Yours faithfully</div>
        <div className="mt-4 fw-semibold">
          {claim.signatory_name || claim.supervisor_name || ""}
        </div>
        <div className="small text-muted">{claim.signatory_role || "Supervisor"}</div>
        {claim.signatory_lbp ? (
          <div className="small text-muted">{claim.signatory_lbp}</div>
        ) : null}
      </div>
      )}

      {/* The INSTRUCTIONS sign-and-return acceptance block used to print here
          (Please proceed as above / Declined / signature lines / RETURN
          EMAIL). REMOVED ENTIRELY (KS, 2026-08-25 meeting): acceptance
          doesn't come back on signed paper any more — insurers instruct
          through their own channels — so the block was dead weight on every
          quote. 1.0 still prints it; this is a deliberate 2.0 departure. */}
      {/* The running identity line (Karen Renall 2026-08-12: every sheet names
          the job) is the table footer at the top of this component. */}
      {/* Page footer line */}
      <div className="small text-muted text-center pt-2" style={{ borderTop: "1px solid #dee2e6" }}>
        {/* "e-mail: x" and "Web: y" wrap as units: the line used to break between "Web:" and
            the address, leaving a lonely "Web:" on the first line (Karen Renall 2026-09-10). */}
        {ISSUER.address.join(" - ")}, Ph:{"\u00a0"}{ISSUER.phone} - <span className="text-nowrap">e-mail: {ISSUER.email}</span> - <span className="text-nowrap">Web: {ISSUER.web}</span>
        {/* The refs used to repeat here. They now ride the running footer above,
            which prints on EVERY page — so on the last sheet the two sat one
            under the other saying the same thing (Robert 2026-08-12). Kept on
            SCREEN, where there is no running footer to carry them. */}
        <div className="d-print-none">
          Your Ref: {claim.your_ref || "—"} · Our Ref: {claim.claim_number}
        </div>
      </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default QuoteView;
